import React from 'react'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import moment from 'moment'

import Inside from './Inside'
import KpiCards from '../components/DashboardV2/KpiCards'
import ExpiringCertificates from '../components/DashboardV2/ExpiringCertificates'
import AuditScheduleDelays from '../components/DashboardV2/AuditScheduleDelays'
import SlaFilterBar, { resolveSlaPeriod } from '../components/DashboardV2/SlaFilterBar'
import SlaPanel from '../components/DashboardV2/SlaPanel'
import OrderTrendChart from '../components/DashboardV2/OrderTrendChart'
import TopActivePanel from '../components/DashboardV2/TopActivePanel'
import '../components/DashboardV2/dashboard-v2.css'

import {
  fetchDashboardSummary,
  fetchDashboardSlaAudit,
  fetchDashboardSlaVeto,
  fetchExpiringCertificates,
  fetchAuditScheduleDelays,
  fetchOrderTrend,
  fetchTopCustomers,
  fetchTopStandards
} from '../actions/dashboardV2'

import {
  getDashboardPanels,
  PANEL_KPI,
  PANEL_EXPIRING,
  PANEL_AUDIT_SCHEDULE,
  PANEL_SLA,
  PANEL_ORDER_TREND,
  PANEL_TOP_ACTIVE
} from '../config/dashboardAccess'

const MONTHS = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
]

/**
 * Dashboard V2 — layout per mockup v2. Filter scoping per requirement:
 * KPI cards, Sertifikat Akan Expire and Jadwal Audit are always current
 * (no filter); the SLA pair shares one LOCAL period filter (US-DB-1);
 * Tren Order and the Top panels each carry their own control. The legacy
 * dashboard stays available, see config/dashboardVersion.js.
 *
 * Which sections a user actually sees is decided by config/dashboardAccess.js;
 * a panel the role may not see is neither rendered nor fetched. The server
 * enforces the same matrix, so hiding here is convenience, not the guard.
 */
export class DashboardV2 extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      slaFilter: {
        mode: 'yearly',
        year: moment().year(),
        month: moment().month()
      },
      trendYear: moment().year(),
      topCustomersMode: 'ytd',
      topStandardsMode: 'ytd'
    }
    this.handleSlaFilterChange = this.handleSlaFilterChange.bind(this)
    this.handleExpiringPage = this.handleExpiringPage.bind(this)
    this.handleTrendYear = this.handleTrendYear.bind(this)
  }

  componentDidMount () {
    const token = this.props.accessToken
    if (this.can(PANEL_KPI)) {
      this.props.fetchDashboardSummary(token)
    }
    if (this.can(PANEL_EXPIRING)) {
      this.props.fetchExpiringCertificates(token, { page: 1 })
    }
    if (this.can(PANEL_AUDIT_SCHEDULE)) {
      this.props.fetchAuditScheduleDelays(token)
    }
    if (this.can(PANEL_SLA)) {
      this.fetchSla()
    }
    if (this.can(PANEL_ORDER_TREND)) {
      this.props.fetchOrderTrend(token, { year: this.state.trendYear })
    }
    if (this.can(PANEL_TOP_ACTIVE)) {
      this.props.fetchTopCustomers(token, { entity: 'customers', mode: this.state.topCustomersMode })
      this.props.fetchTopStandards(token, { entity: 'standards', mode: this.state.topStandardsMode })
    }
  }

  panels () {
    return getDashboardPanels(this.props.user?.data?.role)
  }

  can (panelId) {
    return this.panels().includes(panelId)
  }

  isCustomer () {
    const roles = this.props.user?.data?.role || []
    return roles.includes('Customer')
  }

  fetchSla () {
    const period = resolveSlaPeriod(this.state.slaFilter)
    this.props.fetchDashboardSlaAudit(this.props.accessToken, period)
    this.props.fetchDashboardSlaVeto(this.props.accessToken, period)
  }

  handleSlaFilterChange (slaFilter) {
    this.setState({ slaFilter }, () => this.fetchSla())
  }

  handleExpiringPage (page) {
    this.props.fetchExpiringCertificates(this.props.accessToken, { page })
  }

  handleTrendYear (trendYear) {
    this.setState({ trendYear })
    this.props.fetchOrderTrend(this.props.accessToken, { year: trendYear })
  }

  handleTopMode (which, mode) {
    if (which === 'customers') {
      this.setState({ topCustomersMode: mode })
      this.props.fetchTopCustomers(this.props.accessToken, { entity: 'customers', mode })
    } else {
      this.setState({ topStandardsMode: mode })
      this.props.fetchTopStandards(this.props.accessToken, { entity: 'standards', mode })
    }
  }

  slaPeriodLabel () {
    const { slaFilter } = this.state
    if (slaFilter.mode === 'monthly') {
      return `${MONTHS[slaFilter.month]} ${slaFilter.year}`
    }
    return slaFilter.year === moment().year()
      ? `${slaFilter.year} (YTD)`
      : `${slaFilter.year}`
  }

  render () {
    const {
      summary, slaAudit, slaVeto, expiring,
      scheduleDelays, orderTrend, topCustomers, topStandards
    } = this.props

    // A row2 grid holding a single panel would leave an empty half — collapse
    // it to one column instead.
    const rowClass = (...panels) => {
      const shown = panels.filter((panel) => this.can(panel)).length
      return shown > 1 ? 'dbv2-row2' : 'dbv2-row2 dbv2-row-single'
    }

    return (
      <Inside>
        <Helmet>
          <title>Dashboard</title>
        </Helmet>
        <div className='content'>
          <div className='main-header'>
            <h2>Dashboard</h2>
          </div>
          <div className='main-content dbv2'>
            { this.panels().length === 0
              ? <div className='dbv2-empty'>
                { this.isCustomer()
                  ? 'Dashboard tidak tersedia untuk akun Customer.'
                  : 'Dashboard tidak tersedia untuk akun Anda.'
                }
              </div>
              : <div>
                {/* Always-current KPIs — no filter */}
                { this.can(PANEL_KPI) &&
                  <KpiCards summary={summary.data} loading={summary.loading} />
                }

                {/* Forward-looking — no filter */}
                { (this.can(PANEL_EXPIRING) || this.can(PANEL_AUDIT_SCHEDULE)) &&
                  <div className={`${rowClass(PANEL_EXPIRING, PANEL_AUDIT_SCHEDULE)} dbv2-section-gap`}>
                    { this.can(PANEL_EXPIRING) &&
                      <ExpiringCertificates
                        data={expiring.data}
                        loading={expiring.loading}
                        onPageChange={this.handleExpiringPage}
                      />
                    }
                    { this.can(PANEL_AUDIT_SCHEDULE) &&
                      <AuditScheduleDelays
                        data={scheduleDelays.data}
                        loading={scheduleDelays.loading}
                      />
                    }
                  </div>
                }

                {/* SLA Performance — the only sections the period filter governs */}
                { this.can(PANEL_SLA) &&
                  <div className='dbv2-section-gap'>
                    <div className='dbv2-sla-section-head'>
                      <p className='dbv2-section-label' style={{ margin: 0 }}>Performa SLA</p>
                      <SlaFilterBar
                        filter={this.state.slaFilter}
                        onChange={this.handleSlaFilterChange}
                      />
                    </div>
                    <div className='dbv2-row2'>
                      <SlaPanel
                        title='Capaian SLA Audit'
                        subtitle={`Rata-rata seluruh tahapan, ${this.slaPeriodLabel()}`}
                        data={slaAudit.data}
                        loading={slaAudit.loading}
                      />
                      <SlaPanel
                        title='SLA Veto Review'
                        subtitle='Waktu review/verifikasi dokumen sebelum keputusan sertifikasi'
                        data={slaVeto.data}
                        loading={slaVeto.loading}
                      />
                    </div>
                  </div>
                }

                {/* Tren Order — its own independent year filter */}
                { this.can(PANEL_ORDER_TREND) &&
                  <div className='dbv2-section-gap'>
                    <OrderTrendChart
                      data={orderTrend.data}
                      loading={orderTrend.loading}
                      year={this.state.trendYear}
                      onYearChange={this.handleTrendYear}
                    />
                  </div>
                }

                {/* Composition — each with its own YTD/All-time toggle */}
                { this.can(PANEL_TOP_ACTIVE) &&
                  <div className='dbv2-row2 dbv2-section-gap'>
                    <TopActivePanel
                      title='Top Customers'
                      data={topCustomers.data}
                      loading={topCustomers.loading}
                      mode={this.state.topCustomersMode}
                      onModeChange={(mode) => this.handleTopMode('customers', mode)}
                    />
                    <TopActivePanel
                      title='Top Services / Scheme'
                      data={topStandards.data}
                      loading={topStandards.loading}
                      mode={this.state.topStandardsMode}
                      onModeChange={(mode) => this.handleTopMode('standards', mode)}
                    />
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </Inside>
    )
  }
}

function mapStateToProps (state) {
  return {
    accessToken: state?.auth?.user?.access?.access_token,
    summary: state?.dashboardV2?.summary || {},
    slaAudit: state?.dashboardV2?.slaAudit || {},
    slaVeto: state?.dashboardV2?.slaVeto || {},
    expiring: state?.dashboardV2?.expiring || {},
    scheduleDelays: state?.dashboardV2?.scheduleDelays || {},
    orderTrend: state?.dashboardV2?.orderTrend || {},
    topCustomers: state?.dashboardV2?.topCustomers || {},
    topStandards: state?.dashboardV2?.topStandards || {},
    user: state.auth.user
  }
}

function mapDispatchToProps (dispatch) {
  return {
    fetchDashboardSummary: (token) => dispatch(fetchDashboardSummary(token)),
    fetchDashboardSlaAudit: (token, period) => dispatch(fetchDashboardSlaAudit(token, period)),
    fetchDashboardSlaVeto: (token, period) => dispatch(fetchDashboardSlaVeto(token, period)),
    fetchExpiringCertificates: (token, params) => dispatch(fetchExpiringCertificates(token, params)),
    fetchAuditScheduleDelays: (token) => dispatch(fetchAuditScheduleDelays(token)),
    fetchOrderTrend: (token, params) => dispatch(fetchOrderTrend(token, params)),
    fetchTopCustomers: (token, params) => dispatch(fetchTopCustomers(token, params)),
    fetchTopStandards: (token, params) => dispatch(fetchTopStandards(token, params))
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DashboardV2)
